import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import type { Feature, FeatureCollection, Polygon as GJPolygon } from 'geojson';
import type { MapSelectData } from './map-select.models';
import { INTERSECTION_AREA_EPS } from './map-editor.constants';
import { isClockwise } from './map-geometry.utils';
import {
  GeoCheckFinding,
  outlineFeatures,
  pointFeatures,
  precisionFindingsToLocated,
  precisionLayerPaint,
  TracesFindingItem,
  tracesFindingsToLocated,
  tracesLayerPaint,
} from './traces-finding-layer';

export interface MapLayersContext {
  getMap: () => mapboxgl.Map;
  data: MapSelectData;
  tracesGeometry: () => FeatureCollection | undefined;
  polygonsWithErrors: Set<string>;
  isStyleReady: () => boolean;
  getAllPolygons: () => Feature<GJPolygon>[];
  getLargeDatasetReason: (polys: Feature<GJPolygon>[]) => string | null;
  maybeAnnounceLargeDataset: (reason: string | null, force?: boolean) => void;
  overlapAreaM2: (a: turf.AllGeoJSON, b: turf.AllGeoJSON) => number;
  refreshCtrl: () => void;
}

export class MapLayersController {
  constructor(private readonly context: MapLayersContext) {}
  private get map() {
    return this.context.getMap();
  }
  private get data() {
    return this.context.data;
  }
  private get polygonsWithErrors() {
    return this.context.polygonsWithErrors;
  }
  private isStyleReady() {
    return this.context.isStyleReady();
  }
  private getAllPolygons() {
    return this.context.getAllPolygons();
  }
  private getLargeDatasetReason(polys: Feature<GJPolygon>[]) {
    return this.context.getLargeDatasetReason(polys);
  }
  private maybeAnnounceLargeDataset(reason: string | null, force?: boolean) {
    this.context.maybeAnnounceLargeDataset(reason, force);
  }
  private overlapAreaM2(a: turf.AllGeoJSON, b: turf.AllGeoJSON) {
    return this.context.overlapAreaM2(a, b);
  }
  private refreshCtrl() {
    this.context.refreshCtrl();
  }

  private readonly ERR_SRC_KINKS = 'err-kinks-src';
  private readonly ERR_LAYER_KINKS = 'err-kinks-layer';
  private readonly ERR_SRC_OVERLAPS = 'err-overlaps-src';
  private readonly ERR_LAYER_OVERLAPS_FILL = 'err-overlaps-fill';
  private readonly ERR_LAYER_OVERLAPS_LINE = 'err-overlaps-line';
  private readonly ERR_SRC_BADPOLYS = 'err-badpolys-src';
  private readonly ERR_LAYER_BADPOLYS_LINE = 'err-badpolys-line';
  private readonly ERR_LAYER_BADPOLYS_FILL = 'err-badpolys-fill';

  private readonly ERR_SRC_TRACES = 'err-traces-src';
  private readonly ERR_LAYER_TRACES_LINE = 'err-traces-line';
  private readonly ERR_LAYER_TRACES_POINT = 'err-traces-point';
  private readonly ERR_SRC_PRECISION = 'err-precision-src';
  private readonly ERR_LAYER_PRECISION_LINE = 'err-precision-line';
  private readonly ERR_LAYER_PRECISION_POINT = 'err-precision-point';
  private precisionHiddenAfterEdit = false;
  private setGeoJSON(srcId: string, features: Feature[]) {
    const src = this.map.getSource(srcId) as mapboxgl.GeoJSONSource | undefined;
    if (!src || !this.isStyleReady()) return;
    const data: FeatureCollection = { type: 'FeatureCollection', features: features ?? [] } as any;
    try {
      src.setData(data as any);
    } catch {}
  }

  initErrorHighlightLayers() {
    const addEmptySrc = (id: string) => {
      if (!this.map.getSource(id)) {
        this.map.addSource(id, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      }
    };
    addEmptySrc(this.ERR_SRC_KINKS);
    addEmptySrc(this.ERR_SRC_OVERLAPS);
    addEmptySrc(this.ERR_SRC_BADPOLYS);

    if (!this.map.getLayer(this.ERR_LAYER_KINKS)) {
      this.map.addLayer({
        id: this.ERR_LAYER_KINKS,
        type: 'circle',
        source: this.ERR_SRC_KINKS,
        paint: {
          'circle-radius': 6,
          'circle-color': '#ff1744',
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#fff',
        },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_OVERLAPS_FILL)) {
      this.map.addLayer({
        id: this.ERR_LAYER_OVERLAPS_FILL,
        type: 'fill',
        source: this.ERR_SRC_OVERLAPS,
        paint: { 'fill-color': '#ff1744', 'fill-opacity': 0.35 },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_OVERLAPS_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_OVERLAPS_LINE,
        type: 'line',
        source: this.ERR_SRC_OVERLAPS,
        paint: { 'line-color': '#ff1744', 'line-width': 2.5 },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_BADPOLYS_FILL)) {
      this.map.addLayer({
        id: this.ERR_LAYER_BADPOLYS_FILL,
        type: 'fill',
        source: this.ERR_SRC_BADPOLYS,
        paint: { 'fill-color': '#ff1744', 'fill-opacity': 0.5 },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_BADPOLYS_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_BADPOLYS_LINE,
        type: 'line',
        source: this.ERR_SRC_BADPOLYS,
        paint: { 'line-color': '#c6002b', 'line-width': 2 },
      });
    }

    addEmptySrc(this.ERR_SRC_TRACES);
    addEmptySrc(this.ERR_SRC_PRECISION);

    const tracesPaint = tracesLayerPaint(this.data?.tracesFindingStale ?? false);
    if (!this.map.getLayer(this.ERR_LAYER_TRACES_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_TRACES_LINE,
        type: 'line',
        source: this.ERR_SRC_TRACES,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: tracesPaint.line as any,
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_TRACES_POINT)) {
      this.map.addLayer({
        id: this.ERR_LAYER_TRACES_POINT,
        type: 'circle',
        source: this.ERR_SRC_TRACES,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: tracesPaint.circle as any,
      });
    }

    const precisionPaint = precisionLayerPaint();
    if (!this.map.getLayer(this.ERR_LAYER_PRECISION_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_PRECISION_LINE,
        type: 'line',
        source: this.ERR_SRC_PRECISION,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: precisionPaint.line as any,
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_PRECISION_POINT)) {
      this.map.addLayer({
        id: this.ERR_LAYER_PRECISION_POINT,
        type: 'circle',
        source: this.ERR_SRC_PRECISION,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: precisionPaint.circle as any,
      });
    }

    for (const layerId of [this.ERR_LAYER_TRACES_POINT, this.ERR_LAYER_PRECISION_POINT]) {
      this.map.on('click', layerId, (e) => {
        const feature = e.features?.[0];
        if (feature?.geometry?.type === 'Point') {
          this.map.flyTo({
            center: feature.geometry.coordinates as [number, number],
            zoom: Math.max(this.map.getZoom(), 15),
          });
        }
      });
      this.map.on('mouseenter', layerId, () => (this.map.getCanvas().style.cursor = 'pointer'));
      this.map.on('mouseleave', layerId, () => (this.map.getCanvas().style.cursor = ''));
    }
  }

  renderExternalFindings(): void {
    const geo = this.data?.geojson;

    let tracesItems: TracesFindingItem[] = [];
    if (this.data?.tracesFinding) {
      try {
        const parsed: unknown = JSON.parse(this.data.tracesFinding);
        if (Array.isArray(parsed)) tracesItems = parsed as TracesFindingItem[];
      } catch {}
    }
    const tracesLocated = tracesFindingsToLocated(tracesItems);
    this.setGeoJSON(this.ERR_SRC_TRACES, [...outlineFeatures(geo, tracesLocated), ...pointFeatures(tracesLocated)]);

    let geoFindings: GeoCheckFinding[] = [];
    if (this.data?.geoFindings) {
      try {
        const parsed: unknown = JSON.parse(this.data.geoFindings);
        if (Array.isArray(parsed)) geoFindings = parsed as GeoCheckFinding[];
      } catch {}
    }
    const precisionLocated = this.renderPrecisionFindings(geoFindings, geo);
    this.precisionHiddenAfterEdit = false;

    const firstPoint = pointFeatures(tracesLocated)[0] ?? pointFeatures(precisionLocated)[0];
    if (firstPoint) {
      this.map.flyTo({
        center: firstPoint.geometry.coordinates as [number, number],
        zoom: Math.max(this.map.getZoom(), 14),
      });
    }
  }

  showLivePrecisionFindings(findings: GeoCheckFinding[], site: FeatureCollection): void {
    if (!this.map) return;
    this.renderPrecisionFindings(findings, site);
    this.precisionHiddenAfterEdit = false;
    for (const layerId of [this.ERR_LAYER_PRECISION_LINE, this.ERR_LAYER_PRECISION_POINT]) {
      if (this.map.getLayer(layerId)) this.map.setLayoutProperty(layerId, 'visibility', 'visible');
    }
  }

  private renderPrecisionFindings(findings: GeoCheckFinding[], site: FeatureCollection | undefined) {
    const located = precisionFindingsToLocated(findings, this.context.tracesGeometry());
    this.setGeoJSON(this.ERR_SRC_PRECISION, [...outlineFeatures(site, located), ...pointFeatures(located)]);
    return located;
  }

  hidePrecisionFindingsAfterEdit(): void {
    if (this.precisionHiddenAfterEdit || !this.map) return;
    this.precisionHiddenAfterEdit = true;
    for (const layerId of [this.ERR_LAYER_PRECISION_LINE, this.ERR_LAYER_PRECISION_POINT]) {
      if (this.map.getLayer(layerId)) this.map.setLayoutProperty(layerId, 'visibility', 'none');
    }
  }

  updateErrorHighlights() {
    if (!this.isStyleReady()) return;

    const polys = this.getAllPolygons();
    const skipReason = this.getLargeDatasetReason(polys);
    if (skipReason) {
      this.clearErrorHighlightData();
      this.maybeAnnounceLargeDataset(skipReason, false);
      return;
    }

    const kinkPoints: Feature[] = [];
    const overlapFeats: Feature[] = [];
    const badPolySet = new Set<number>();

    for (let i = 0; i < polys.length; i++) {
      try {
        const k = turf.kinks(polys[i] as any) as FeatureCollection;
        const feats = k?.features || [];
        if (feats.length) {
          badPolySet.add(i);
          for (let f = 0; f < feats.length; f++) {
            const g = feats[f].geometry?.type;
            if (g === 'Point' || g === 'MultiPoint') kinkPoints.push(feats[f] as Feature);
          }
        }
      } catch {}
    }

    for (let i = 0; i < polys.length; i++) {
      for (let j = i + 1; j < polys.length; j++) {
        const area = this.overlapAreaM2(polys[i] as any, polys[j] as any);
        if (area > INTERSECTION_AREA_EPS) {
          let inter: Feature | null = null;
          try {
            inter = turf.intersect(turf.featureCollection([polys[i], polys[j]] as any)) as Feature | null;
          } catch {}
          if (!inter) {
            const ai = turf.area(polys[i] as any);
            const aj = turf.area(polys[j] as any);
            inter = (ai <= aj ? polys[i] : polys[j]) as unknown as Feature;
          }
          overlapFeats.push(inter as Feature);
          badPolySet.add(i);
          badPolySet.add(j);
        }
      }
    }

    for (let i = 0; i < polys.length; i++) {
      const coords = (polys[i].geometry.coordinates ?? []) as [number, number][][];
      if (!coords.length) continue;
      if (!isClockwise(coords[0])) badPolySet.add(i);
      for (let r = 1; r < coords.length; r++) if (isClockwise(coords[r])) badPolySet.add(i);
    }

    const badPolyFeats: Feature[] = Array.from(badPolySet).map((idx) => polys[idx]) as Feature[];
    this.polygonsWithErrors.clear();
    for (const idx of badPolySet) {
      const id = (polys[idx] as any)?.id;
      if (id != null) this.polygonsWithErrors.add(String(id));
    }

    this.setGeoJSON(this.ERR_SRC_KINKS, kinkPoints);
    this.setGeoJSON(this.ERR_SRC_OVERLAPS, overlapFeats);
    this.setGeoJSON(this.ERR_SRC_BADPOLYS, badPolyFeats);
    this.refreshCtrl();
  }

  clearErrorHighlightData() {
    this.setGeoJSON(this.ERR_SRC_KINKS, []);
    this.setGeoJSON(this.ERR_SRC_OVERLAPS, []);
    this.setGeoJSON(this.ERR_SRC_BADPOLYS, []);
    this.polygonsWithErrors.clear();
    this.refreshCtrl();
  }
}
